/**
 * The ingredient list — the one COMPOUND fact this surface reads and authors.
 *
 * WHY THIS FILE EXISTS
 * `nutrition.active_ingredients` is stored as an object envelope, not a scalar:
 *
 *   { "items": [ { "name": "C8 MCT Oil", "amount_mg": 16975, "order": 1 },
 *                { "name": "Arabic Gum", "amount_mg": 8345,  "order": 2 } ] }
 *
 * (`execution/2026-09-10-engb-panel-facts/load_tmpl.py`, the loader that wrote
 * every live row.) The module had no concept of it, and both halves of the
 * surface were wrong in a way no screenshot could catch:
 *
 *   • DISPLAY — `formatValue` fell through to `JSON.stringify`, so the figure a
 *     customer sees rendered on the LIVE NOW card as a wall of braces.
 *   • AUTHORING — one "New value" textbox fed `draftValueFor`, which coerces
 *     anything non-numeric to `{ s: <the typed string> }`. Typing into it did
 *     not fail; it SAVED, as a string, under a key the store holds as an
 *     object. A corrupt shape that reads as a real value downstream is worse
 *     than the JSON it replaced.
 *
 * So the list is parsed into rows here, rendered as prose here, and edited as
 * rows — name, order, amount — by `IngredientsField`. A JSON textarea is never
 * the authoring UI for it (KAL-355, HARD).
 */

/**
 * One ingredient AS READ from the store.
 *
 * `amount_mg` is nullable on the READ side only: the surface renders whatever
 * the store actually holds, and a row that arrived without an amount must show
 * as a named ingredient with no amount rather than as a fabricated `0 mg`. The
 * SAVE side refuses it — see `ingredientsToValue`.
 */
export type Ingredient = { name: string; amount_mg: number | null; order: number };

/**
 * The rows in an ingredient-shaped envelope, in the order they are declared —
 * or `null` when the value is not that shape.
 *
 * `null` is the answer for every scalar and for an object of some other shape,
 * and callers branch on it rather than guessing: mis-reading a different object
 * as an ingredient list would put a structured editor in front of a value it
 * cannot represent.
 *
 * An EMPTY `items` array is an ingredient list, not a non-answer — it returns
 * `[]`, and the pane says "no ingredients listed" rather than falling back to
 * the scalar field.
 */
export function ingredientsIn(
  value: Record<string, unknown> | null | undefined,
): Ingredient[] | null {
  if (!value || !Array.isArray(value.items)) return null;
  const rows: Ingredient[] = [];
  for (const [index, raw] of value.items.entries()) {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
    const entry = raw as Record<string, unknown>;
    // The NAME is the one field that makes a row an ingredient. Without it
    // there is nothing to show a reader, so the whole envelope is some other
    // shape and the caller must not treat it as this one.
    if (typeof entry.name !== "string") return null;
    rows.push({
      name: entry.name,
      amount_mg: finite(entry.amount_mg),
      // A row with no declared order keeps its position in the array rather
      // than collapsing to 0 — renumbering every unordered row to the same
      // place would shuffle the list on the next sort.
      order: finite(entry.order) ?? index + 1,
    });
  }
  return rows.sort((a, b) => a.order - b.order);
}

/**
 * Whether the row editor can hold EVERYTHING in this envelope — `ingredientsIn`
 * reading it is not enough.
 *
 * The editor has three boxes per row: name, position, milligrams. A save
 * rebuilds the envelope from those boxes alone, so any other field — an
 * allergen flag on an item, a note beside `items` — is dropped on the first
 * save with no warning. A value the editor cannot represent in full is shown,
 * not edited: the one screen whose job is correcting customer-facing numbers
 * must not overwrite what it cannot show.
 */
const ITEM_FIELDS = new Set(["name", "amount_mg", "order"]);

export function ingredientsEditable(value: Record<string, unknown> | null | undefined): boolean {
  if (ingredientsIn(value) === null || value == null) return false;
  if (Object.keys(value).some((key) => key !== "items")) return false;
  return (value.items as Array<Record<string, unknown>>).every((item) =>
    Object.keys(item).every((key) => ITEM_FIELDS.has(key)),
  );
}

function finite(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}

/**
 * The list as one line of prose — what the table cell, the diff and the history
 * print. "C8 MCT Oil 16,975 mg · Arabic Gum 8,345 mg".
 *
 * Thousands are grouped because these are four- and five-digit milligram
 * figures and `16975` is read wrong at a glance. An amount the store never held
 * renders as the bare name: "no amount" is a true statement, `0 mg` is a false
 * one.
 */
export function formatIngredients(rows: Ingredient[]): string {
  if (rows.length === 0) return "No ingredients listed";
  return rows
    .map((row) => (row.amount_mg === null ? row.name : `${row.name} ${milligrams(row.amount_mg)}`))
    .join(" · ");
}

export function milligrams(amount: number): string {
  return `${amount.toLocaleString("en-GB")} mg`;
}

/**
 * One row of the EDITOR, as typed.
 *
 * Every field is a string because every field is an `<input>`: a half-typed
 * amount is `"16"`, and a row being added is empty. Coercing to numbers in
 * state would make a cleared field read as `0` — a published amount nobody
 * entered. Numbers are parsed once, at the boundary, by `ingredientsToValue`.
 *
 * `key` is a render identity, never saved. Rows are dragged, inserted and
 * deleted, so keying on the index would move React's state to the wrong row
 * mid-edit; keying on the name would collide across two blank new rows.
 */
export type IngredientDraft = { key: string; name: string; amountMg: string; order: string };

let nextKey = 0;

export function blankIngredient(order: number): IngredientDraft {
  nextKey += 1;
  return { key: `new-${nextKey}`, name: "", amountMg: "", order: String(order) };
}

/** The stored list, as the editor's starting rows. */
export function draftRowsFrom(rows: Ingredient[]): IngredientDraft[] {
  return rows.map((row, index) => ({
    key: `row-${index}`,
    name: row.name,
    amountMg: row.amount_mg === null ? "" : String(row.amount_mg),
    order: String(row.order),
  }));
}

/**
 * The typed rows as the envelope C2 stores, or ONE message naming what to fix.
 *
 * Validation is real here rather than advisory, and that is the change of
 * substance: the string path this replaces could not fail, so an ingredient
 * with no amount saved happily as part of a corrupt string. Each refusal names
 * the offending ingredient, because "a row is invalid" in a nine-row list is
 * not a next step.
 */
/**
 * Which box the refusal is about, so the editor can outline THAT box in red —
 * the scalar field's 422 treatment. A sentence under a nine-row list that names
 * an ingredient still leaves the reader hunting for the empty cell.
 */
export interface InvalidCell {
  index: number;
  field: "name" | "amountMg" | "order";
}

export function ingredientsToValue(
  rows: IngredientDraft[],
): { value: Record<string, unknown> } | { error: string; at?: InvalidCell } {
  if (rows.length === 0) {
    return { error: "Add at least one ingredient — an empty list publishes nothing." };
  }
  const items: Array<{ name: string; amount_mg: number; order: number }> = [];
  for (const [index, row] of rows.entries()) {
    const name = row.name.trim();
    if (name === "") {
      return {
        error: `Row ${index + 1} has no name. Every ingredient needs one.`,
        at: { index, field: "name" },
      };
    }
    const amount = Number(row.amountMg.trim());
    if (row.amountMg.trim() === "" || !Number.isFinite(amount)) {
      return {
        error: `“${name}” has no amount. Enter the milligrams as digits, e.g. 16975.`,
        at: { index, field: "amountMg" },
      };
    }
    if (amount < 0) {
      return {
        error: `“${name}” has a negative amount. Enter the milligrams as digits.`,
        at: { index, field: "amountMg" },
      };
    }
    const order = Number(row.order.trim());
    if (row.order.trim() === "" || !Number.isInteger(order) || order < 1) {
      return {
        error: `“${name}” needs a whole position number, 1 or higher.`,
        at: { index, field: "order" },
      };
    }
    items.push({ name, amount_mg: amount, order });
  }
  const orders = items.map((item) => item.order);
  if (new Set(orders).size !== orders.length) {
    // Two rows claiming one place is not a tie to break silently: the declared
    // order is what prints on the pack, and picking a winner here would publish
    // an order nobody chose.
    return {
      error: "Two ingredients share a position. Give each row its own place in the list.",
    };
  }
  return { value: { items: [...items].sort((a, b) => a.order - b.order) } };
}

/**
 * The typed rows as ONE string, for the draft machinery only.
 *
 * The pane tracks a correction as a single `nextValue` string: it is what says
 * whether anything has been typed, what a queued draft carries, and what
 * `draft.input === nextValue` compares to decide a re-draft is needed. Those
 * three are subtle and already correct, so the editor feeds them an encoded
 * form rather than a second parallel state that could disagree with the first.
 *
 * It is NEVER rendered and never sent — `buildValue` decodes it and sends the
 * structured envelope. This is a state key, not an authoring surface, and the
 * rule it must not break is that no person ever types or reads it.
 */
export function encodeRows(rows: IngredientDraft[]): string {
  return JSON.stringify(rows.map((row) => [row.name, row.amountMg, row.order]));
}

export function decodeRows(encoded: string): IngredientDraft[] {
  try {
    const parsed: unknown = JSON.parse(encoded);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((entry, index) => {
      const [name, amountMg, order] = Array.isArray(entry) ? entry : [];
      return {
        key: `enc-${index}`,
        name: typeof name === "string" ? name : "",
        amountMg: typeof amountMg === "string" ? amountMg : "",
        order: typeof order === "string" ? order : String(index + 1),
      };
    });
  } catch {
    return [];
  }
}

/**
 * Whether two sets of editor rows are the SAME ingredient list.
 *
 * Compared as what they would publish, not as typed text: the same names and
 * amounts in the same relative order. Position numbers count only as an order —
 * a store holding 10, 20 and an editor renumbered to 1, 2 describe one list —
 * and "16975" and "16975.0" are one amount. Rows the validator refuses are
 * compared as typed, since there is no published form to compare.
 *
 * This is how the pane tells a correction from focus moving across it. Reading
 * a renumber or an edit-then-revert as a correction left the pane holding a
 * "changed" list that it would never draft, and Approve waited on that draft
 * for good.
 */
export function sameIngredientList(a: IngredientDraft[], b: IngredientDraft[]): boolean {
  return listIdentity(a) === listIdentity(b);
}

function listIdentity(rows: IngredientDraft[]): string {
  const built = ingredientsToValue(rows);
  if (!("value" in built)) return `typed:${encodeRows(rows)}`;
  const items = built.value.items as Array<{ name: string; amount_mg: number }>;
  return `list:${JSON.stringify(items.map((item) => [item.name, item.amount_mg]))}`;
}

/**
 * Renumber to 1…n in the order the rows now sit.
 *
 * Called after every drag, move and delete, so the POSITION a reader sees and
 * the number that gets published are the same fact. Two sources of truth for
 * "which ingredient comes first" is how a list publishes in an order nobody saw.
 */
export function renumber(rows: IngredientDraft[]): IngredientDraft[] {
  return rows.map((row, index) => ({ ...row, order: String(index + 1) }));
}

/** Move a row to a new index, then renumber. Used by both drag and the keyboard. */
export function moveRow(rows: IngredientDraft[], from: number, to: number): IngredientDraft[] {
  if (from === to || from < 0 || from >= rows.length || to < 0 || to >= rows.length) return rows;
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return renumber(next);
}

/**
 * Re-sort after a position was typed, then renumber — what leaving the position
 * field does.
 *
 * Typing "3" into the first row's position is a request to move that row, and a
 * list that kept it on top while printing 3 beside it would be showing one order
 * and publishing another. Re-sorting happens on BLUR rather than per keystroke,
 * because a row that jumps while a digit is half-typed cannot be edited.
 *
 * `moved` is the row whose position was typed. It lands AT the typed place and
 * every other row keeps its order around it. Sorting all rows by number and
 * breaking ties by current index got this wrong: in A 1, B 2, C 3, typing 1
 * into C tied C with A, the tie went to A, and the list read A, C, B — a pack
 * order the operator did not type. Without `moved`, every row is placed by its
 * own number and a tie keeps the current order.
 */
export function sortByTypedOrder(rows: IngredientDraft[], moved?: number): IngredientDraft[] {
  const numbered = rows.map((row, index) => {
    const typed = row.order.trim();
    // `Number("")` is 0, NOT NaN. Without the explicit blank test a row whose
    // position has just been cleared sorts silently to the FRONT of the list,
    // and the operator watches the row they are editing jump while they are
    // mid-way through retyping its number.
    return { row, index, order: typed === "" ? Number.NaN : Number(typed) };
  });
  // A row whose position is blank or not a number keeps its current place —
  // there is nothing to sort it by, and NaN would send it to an arbitrary end.
  if (numbered.some((entry) => !Number.isFinite(entry.order))) return rows;
  const byNumber = (a: (typeof numbered)[number], b: (typeof numbered)[number]) =>
    a.order - b.order || a.index - b.index;

  if (moved === undefined || moved < 0 || moved >= rows.length) {
    return renumber([...numbered].sort(byNumber).map((entry) => entry.row));
  }
  const target = numbered[moved].order;
  // A position that is not a whole number from 1 is left for the validator to
  // name, not quietly rounded into a place the operator did not type.
  if (!Number.isInteger(target) || target < 1) return rows;
  const others = numbered
    .filter((entry) => entry.index !== moved)
    .sort(byNumber)
    .map((entry) => entry.row);
  const at = Math.min(target, rows.length) - 1;
  return renumber([...others.slice(0, at), rows[moved], ...others.slice(at)]);
}
